import { useEffect, useState } from "react";
import { loadStripe, Stripe } from "@stripe/stripe-js";
import type { PaymentGateway, PaymentMethod } from "types";

export type SelectedCheckoutPaymentMethod = {
  name: string;
  _id: string;
};

export type UseCheckoutPaymentParams = {
  token: string;
  supplierId: string;
  payerUserId: string;
  orderId: string;
  totalAmount: number;
  enabled: boolean;
};

export function useCheckoutPayment({
  token,
  supplierId,
  payerUserId,
  orderId,
  totalAmount,
  enabled,
}: UseCheckoutPaymentParams) {
  const [integrations, setIntegrations] = useState<PaymentGateway[]>([]);
  const [dynamicMethods, setDynamicMethods] = useState<PaymentMethod[]>([]);
  const [stripePromise, setStripePromise] =
    useState<Promise<Stripe | null> | null>(null);
  const [clientSecret, setClientSecret] = useState("");
  const [selectedPaymentMethod, setSelectedPaymentMethod] =
    useState<SelectedCheckoutPaymentMethod>({ name: "online", _id: "online" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!enabled || !token || !supplierId) return;

    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/payments/gateway/created-by/${supplierId}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        if (!response.ok) throw new Error("Failed to fetch payment gateways");
        const data = await response.json();
        if (!cancelled) setIntegrations(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setIntegrations([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled, token, supplierId]);

  useEffect(() => {
    const stripeInfo = integrations.find((item) => item.gateway === "stripe");
    if (stripeInfo?.publishable_key) {
      setStripePromise(loadStripe(stripeInfo.publishable_key));
    } else {
      setStripePromise(null);
    }
  }, [integrations]);

  useEffect(() => {
    if (!enabled || !token || !supplierId || !payerUserId) return;

    let cancelled = false;
    (async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/payment-methods/by-supplier/${supplierId}/by-user/${payerUserId}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        if (!response.ok) throw new Error("Failed to fetch payment methods");
        const data = await response.json();
        if (!cancelled) setDynamicMethods(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setDynamicMethods([]);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled, token, supplierId, payerUserId]);

  useEffect(() => {
    if (!enabled || !stripePromise || !supplierId || !orderId || totalAmount <= 0) {
      setClientSecret("");
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/payments/stripe/payment-intent/${supplierId}`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              amount: Math.round(Number(totalAmount) * 100),
              currency: "usd",
              payment_method_types: ["card"],
            }),
          }
        );
        if (!response.ok) throw new Error("Failed to create payment intent");
        const data = await response.json();
        if (!cancelled) {
          setClientSecret(String(data.clientSecret || data.client_secret || ""));
        }
      } catch {
        if (!cancelled) setClientSecret("");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled, stripePromise, supplierId, orderId, totalAmount, token]);

  return {
    dynamicMethods,
    stripePromise,
    clientSecret,
    selectedPaymentMethod,
    setSelectedPaymentMethod,
    loading,
  };
}
